import * as effects from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import * as actions from './actions'
import * as api from './api'
import { AuthorizationType, UpdateProfileType } from './types'
import Cookies from 'js-cookie'
import * as Sentry from '@sentry/react'
import { formatErrorMessage, downloadFile } from '../../utils/helpers'
import history from '../../utils/history'

const { REACT_APP_TIMEOUT_FETCH_NEW_ACHIEVEMENTS } = process.env

function* signIn(payload: AuthorizationType): SagaIterator {
  try {
    const { code, redirect_uri, came_from } = payload
    const data = yield effects.call(api.signIn, code, redirect_uri)
    const token = data.token
    if (token) {
      Cookies.set('token', token, { expires: 365 })
      if (came_from) {
        yield effects.call([history, history.push], came_from)
      } else {
        yield effects.call([history, history.push], '/')
      }
      yield effects.put(actions.signInSuccess())
      yield effects.put(actions.fetchProfile())
    } else {
      yield effects.put(actions.signInFailure(data.message))
    }
  } catch (error) {
    console.warn('Cannot send authorization code', error)
    yield effects.put(actions.signInFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* signOut(): SagaIterator {
  try {
    const redirectUrl = process.env.REACT_APP_SSO_LOGOUT_REDIRECT_URL
    const data = yield effects.call(api.signOut, redirectUrl)
    Cookies.remove('token')
    yield effects.put(actions.signOutSuccess())
    if (data.logout_url) {
      window.location.href = data.logout_url
    } else {
      yield effects.call([history, history.push], '/signin')
    }
  } catch (error) {
    console.warn('Cannot send logout', error)
    yield effects.put(actions.signOutFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* checkFirstModuleComplete(payload: { id: string }): SagaIterator {
  try {
    const result = yield effects.call(api.fetchCourseUserState, payload.id)
    const moduleIsComplete = result.data.moduleProgress.completed > 0
    yield effects.put(actions.checkFirstModuleCompleteSuccess(moduleIsComplete))
  } catch (error) {
    console.warn('Cannot check first module complete', error)
    yield effects.put(
      actions.checkFirstModuleCompleteFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchProfile(): SagaIterator {
  try {
    const profile = yield effects.call(api.fetchProfile)
    yield effects.put(actions.fetchProfileSuccess(profile.data))
  } catch (error) {
    console.warn('Cannot fetch profile', error)
    if (error.response?.code === 401) {
      Cookies.remove('token')
      yield effects.put(actions.signOutSuccess())
      yield effects.call([history, history.push], '/signin')
    }
    yield effects.put(actions.fetchProfileFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchNewAchievements(): SagaIterator {
  try {
    while (true) {
      const achievements = yield effects.call(api.fetchNewAchievements)
      yield effects.put(actions.fetchNewAchievementsSuccess(achievements.data))
      yield effects.delay(parseInt(REACT_APP_TIMEOUT_FETCH_NEW_ACHIEVEMENTS))
    }
  } catch (error) {
    console.warn('Cannot fetch new achievements', error)
    if (error.response?.code === 401) {
      Cookies.remove('token')
      yield effects.put(actions.signOutSuccess())
      yield effects.call([history, history.push], '/signin')
    }
    yield effects.put(
      actions.fetchNewAchievementsFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* markViewedAchievement(payload: { id: string }): SagaIterator {
  try {
    yield effects.call(api.markViewedAchievement, payload.id)
    yield effects.put(actions.markViewedAchievementSuccess())
  } catch (error) {
    console.warn('Cannot mark viewed achievement', error)
    yield effects.put(
      actions.markViewedAchievementFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* saveProfile(payload: { data: UpdateProfileType }): SagaIterator {
  try {
    yield effects.call(api.saveProfile, payload.data)
    yield effects.put(actions.saveProfileSuccess())
    yield effects.put(actions.fetchProfile())
    yield effects.put(actions.fetchNewAchievements())
  } catch (error) {
    console.warn('Cannot save profile', error)
    yield effects.put(actions.saveProfileFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* uploadPhoto(payload: { photo: File }): SagaIterator {
  try {
    const { photo } = payload
    yield effects.call(api.uploadPhoto, photo)
    yield effects.put(actions.uploadPhotoSuccess())
    yield effects.put(actions.fetchProfile())
  } catch (error) {
    console.warn('Cannot upload photo', error)
    yield effects.put(actions.uploadPhotoFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removePhoto(): SagaIterator {
  try {
    yield effects.call(api.removePhoto)
    yield effects.put(actions.removePhotoSuccess())
    yield effects.put(actions.fetchProfile())
  } catch (error) {
    console.warn('Cannot remove photo', error)
    yield effects.put(actions.removePhotoFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchReferralLink(payload: {
  typeReferral: string
  redirect_url: string
  is_reverse: string
  connection_type?: string
}): SagaIterator {
  try {
    const { typeReferral, redirect_url, is_reverse, connection_type } = payload
    const data = yield effects.call(
      api.fetchReferralLink,
      typeReferral,
      redirect_url,
      is_reverse,
      connection_type
    )
    yield effects.put(
      actions.fetchReferralLinkSuccess(data.referral_link_object)
    )
  } catch (error) {
    console.warn('Cannot fetch referral link', error)
    yield effects.put(
      actions.fetchReferralLinkFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* sendParentInvite(payload: {
  parent: 'father' | 'mother'
  email: string
}): SagaIterator {
  try {
    const {
      REACT_APP_URL,
      REACT_APP_URI_PREFIX,
      REACT_APP_INVITE_MESSAGE_FATHER,
      REACT_APP_INVITE_MESSAGE_MOTHER,
    } = process.env
    const { parent, email } = payload
    const data = yield effects.call(
      api.fetchReferralLink,
      `parent:${parent}`,
      `${REACT_APP_URL}${REACT_APP_URI_PREFIX}/signin`,
      '1',
      'one-to-many'
    )
    yield effects.call(
      api.sendEmailInvite,
      data.referral_link_object.code,
      parent === 'father'
        ? REACT_APP_INVITE_MESSAGE_FATHER
        : REACT_APP_INVITE_MESSAGE_MOTHER,
      email
    )
    yield effects.put(actions.sendParentInviteSuccess())
  } catch (error) {
    console.warn('Cannot send parent invite', error)
    yield effects.put(
      actions.sendParentInviteFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* uploadAchievementScreenshot(payload: {
  achievementId: string
  image: File
}): SagaIterator {
  try {
    const { achievementId, image } = payload
    const data = yield effects.call(
      api.uploadAchievementScreenshot,
      achievementId,
      image
    )
    yield effects.call(
      downloadFile,
      data.data.user_achievement.attachment.url,
      `achievement-${data.data.code}.png`
    )
    yield effects.put(actions.uploadAchievementScreenshotSuccess())
  } catch (error) {
    console.warn('Cannot upload achievement screenshot', error)
    yield effects.put(
      actions.uploadAchievementScreenshotFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* sendEmailInvite(payload: {
  code: string
  email: string
  text: string
}): SagaIterator {
  try {
    const { code, email, text } = payload
    yield effects.call(api.sendEmailInvite, code, text, email)
    yield effects.put(actions.sendEmailInviteSuccess())
  } catch (error) {
    console.warn('Cannot send email invite', error)
    yield effects.put(actions.sendEmailInviteFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* shareAchievementScreenshot(payload: {
  achievementId: string
  image: File
}): SagaIterator {
  try {
    const { achievementId, image } = payload
    const data = yield effects.call(
      api.uploadAchievementScreenshot,
      achievementId,
      image
    )
    yield effects.put(
      actions.shareAchievementScreenshotSuccess(
        data.data.user_achievement.attachment.url
      )
    )
  } catch (error) {
    console.warn('Cannot share achievement screenshot', error)
    yield effects.put(
      actions.shareAchievementScreenshotFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* changeProfileRole(payload: {
  typeRole: 'teacher' | 'student'
}): SagaIterator {
  try {
    if (payload.typeRole === 'teacher') {
      yield effects.call(api.setProfileTeacher)
    } else {
      yield effects.call(api.setProfileStudent)
    }
    yield effects.put(actions.changeProfileRoleSuccess())
  } catch (error) {
    console.warn('Cannot set change profile role', error)
    yield effects.put(
      actions.changeProfileRoleFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* mainProfile(): SagaIterator {
  while (yield effects.take(actions.fetchProfileSuccess)) {
    const bgSyncAchievements = yield effects.fork(fetchNewAchievements)
    yield effects.take([actions.fetchProfileFailure, actions.signOutSuccess])
    yield effects.cancel(bgSyncAchievements)
  }
}

function* fetchCertificates(): SagaIterator {
  try {
    const certificates = yield effects.call(api.fetchCertificates)
    yield effects.put(actions.fetchCertificatesSuccess(certificates.data))
  } catch (error) {
    console.warn('Cannot fetch certificates', error)
    yield effects.put(
      actions.fetchCertificatesFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* watchSignIn(): SagaIterator {
  yield effects.takeEvery(actions.signIn.toString(), signIn)
}

function* watchSignOut(): SagaIterator {
  yield effects.takeEvery(actions.signOut.toString(), signOut)
}

function* watchCheckFirstModuleComplete(): SagaIterator {
  yield effects.takeEvery(
    actions.checkFirstModuleComplete.toString(),
    checkFirstModuleComplete
  )
}

function* watchFetchProfile(): SagaIterator {
  yield effects.takeEvery(actions.fetchProfile.toString(), fetchProfile)
}

function* watchFetchNewAchievements(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchNewAchievements.toString(),
    fetchNewAchievements
  )
}

function* watchMarkViewedAchievement(): SagaIterator {
  yield effects.takeEvery(
    actions.markViewedAchievement.toString(),
    markViewedAchievement
  )
}

function* watchSaveProfile(): SagaIterator {
  yield effects.takeEvery(actions.saveProfile.toString(), saveProfile)
}

function* watchUploadPhoto(): SagaIterator {
  yield effects.takeEvery(actions.uploadPhoto.toString(), uploadPhoto)
}

function* watchRemovePhoto(): SagaIterator {
  yield effects.takeEvery(actions.removePhoto.toString(), removePhoto)
}

function* watchSendParentInvite(): SagaIterator {
  yield effects.takeEvery(actions.sendParentInvite.toString(), sendParentInvite)
}

function* watchFetchReferralLink(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchReferralLink.toString(),
    fetchReferralLink
  )
}

function* watchUploadAchievementScreenshot(): SagaIterator {
  yield effects.takeEvery(
    actions.uploadAchievementScreenshot.toString(),
    uploadAchievementScreenshot
  )
}

function* watchSendEmailInvite(): SagaIterator {
  yield effects.takeEvery(actions.sendEmailInvite.toString(), sendEmailInvite)
}

function* watchShareAchievementScreenshot(): SagaIterator {
  yield effects.takeEvery(
    actions.shareAchievementScreenshot.toString(),
    shareAchievementScreenshot
  )
}

function* watchChangeProfileRole(): SagaIterator {
  yield effects.takeEvery(
    actions.changeProfileRole.toString(),
    changeProfileRole
  )
}

function* watchFetchCertificates(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchCertificates.toString(),
    fetchCertificates
  )
}

export default function* watchProfile(): SagaIterator {
  yield effects.all([
    effects.fork(watchSignIn),
    effects.fork(watchSignOut),
    effects.fork(watchCheckFirstModuleComplete),
    effects.fork(watchFetchProfile),
    effects.fork(watchFetchNewAchievements),
    effects.fork(watchMarkViewedAchievement),
    effects.fork(watchSaveProfile),
    effects.fork(watchUploadPhoto),
    effects.fork(watchRemovePhoto),
    effects.fork(watchFetchReferralLink),
    effects.fork(watchSendParentInvite),
    effects.fork(watchUploadAchievementScreenshot),
    effects.fork(watchSendEmailInvite),
    effects.fork(watchShareAchievementScreenshot),
    effects.fork(watchChangeProfileRole),
    effects.fork(watchFetchCertificates),
    effects.fork(mainProfile),
  ])
}
